import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import cx from "clsx";
import { useCallback, useEffect, useId, useMemo, useRef, useState, } from "react";
import Pill from "@ingka/pill";
import Search from "@ingka/search";
import chevronDownSmall from "@ingka/ssr-icon/paths/chevron-down-small";
import chevronUpSmall from "@ingka/ssr-icon/paths/chevron-up-small";
import Text from "@ingka/text";
import { filterSearchableSelectOptions, groupSearchableSelectOptions, } from "./SearchableSelect.utils";
export const SearchableSelect = ({ options, value, onChange, label, placeholder, searchPlaceholder, emptyMessage, ariaLabel, disabled = false, isLoading = false, id, className, }) => {
    const generatedId = useId();
    const instanceId = id ?? generatedId;
    const listId = `${instanceId}-list`;
    const searchId = `${instanceId}-search`;
    const [isOpen, setIsOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const containerRef = useRef(null);
    const searchWrapRef = useRef(null);
    const selectedOption = useMemo(() => options.find((option) => option.value === value), [options, value]);
    const pillLabel = selectedOption?.label ?? placeholder ?? "";
    const listAriaLabel = ariaLabel ?? label ?? placeholder ?? pillLabel;
    const groupedOptions = useMemo(() => groupSearchableSelectOptions(filterSearchableSelectOptions(options, searchQuery)), [options, searchQuery]);
    const isTriggerDisabled = disabled || isLoading;
    const toggleOpen = useCallback(() => {
        if (isTriggerDisabled) {
            return;
        }
        setIsOpen((previous) => !previous);
    }, [isTriggerDisabled]);
    const close = useCallback(() => {
        setIsOpen(false);
        setSearchQuery("");
    }, []);
    const handleSearchChange = useCallback((event) => {
        setSearchQuery(event.target.value);
    }, []);
    const handleSelect = useCallback((optionValue) => {
        onChange(optionValue);
        close();
    }, [close, onChange]);
    useEffect(() => {
        if (!isOpen) {
            return undefined;
        }
        const handlePointerDown = (event) => {
            const container = containerRef.current;
            if (container === null) {
                return;
            }
            const path = event.composedPath();
            if (path.includes(container)) {
                return;
            }
            close();
        };
        document.addEventListener("mousedown", handlePointerDown);
        document.addEventListener("touchstart", handlePointerDown);
        return () => {
            document.removeEventListener("mousedown", handlePointerDown);
            document.removeEventListener("touchstart", handlePointerDown);
        };
    }, [close, isOpen]);
    useEffect(() => {
        if (!isOpen) {
            return undefined;
        }
        const frame = requestAnimationFrame(() => {
            const input = searchWrapRef.current?.querySelector("input");
            input?.focus();
        });
        return () => cancelAnimationFrame(frame);
    }, [isOpen]);
    return (_jsxs("div", { ref: containerRef, className: cx("pdpp-react-searchableSelect", className), "data-auto-id": id, children: [label !== undefined && label.length > 0 && (_jsx(Text, { className: "pdpp-react-searchableSelectLabel", tagName: "span", textStyle: "Body.M", children: label })), _jsx("div", { className: "pdpp-react-searchableSelectTrigger", children: _jsx(Pill, { label: _jsx(Text, { tagName: "span", textStyle: "Label.M", children: pillLabel }), size: "small", disabled: isTriggerDisabled, onClick: toggleOpen, "aria-haspopup": "listbox", "aria-expanded": isOpen, "aria-controls": listId, trailingIcon: isOpen ? chevronUpSmall : chevronDownSmall }) }), isOpen && (_jsxs("div", { className: "pdpp-react-searchableSelectPopover", id: listId, role: "listbox", "aria-label": listAriaLabel, children: [_jsx("div", { ref: searchWrapRef, className: "pdpp-react-searchableSelectSearchWrap", children: _jsx(Search, { id: searchId, className: "pdpp-react-searchableSelectSearch", value: searchQuery, onChange: handleSearchChange, onClear: () => setSearchQuery(""), placeholder: searchPlaceholder }) }), _jsx("div", { className: "pdpp-react-searchableSelectList", children: groupedOptions.length === 0 ? (_jsx(Text, { tagName: "p", textStyle: "Body.S", className: "pdpp-react-searchableSelectEmpty", children: emptyMessage })) : (groupedOptions.map(([category, categoryOptions]) => (_jsxs("div", { className: "pdpp-react-searchableSelectGroup", children: [category !== undefined && (_jsx(Text, { tagName: "p", textStyle: "Heading.XS", className: "pdpp-react-searchableSelectGroupTitle", children: category })), _jsx("ul", { className: "pdpp-react-searchableSelectItems", children: categoryOptions.map((option) => {
                                        const isSelected = value === option.value;
                                        return (_jsx("li", { children: _jsx("button", { type: "button", role: "option", "aria-selected": isSelected, className: cx("pdpp-react-searchableSelectItem", {
                                                    "pdpp-react-searchableSelectItemSelected": isSelected,
                                                }), onClick: () => handleSelect(option.value), children: _jsx(Text, { tagName: "span", textStyle: "Body.M", children: option.label }) }) }, option.value));
                                    }) })] }, category ?? "ungrouped")))) })] }))] }));
};
//# sourceMappingURL=SearchableSelect.js.map